import { RoleCode, BusinessOperatingSystem } from '@ninety/mobile/platform/api';

export enum NinetyPlatform {
  Web = 'web',
  Mobile = 'mobile',
}

/**
 * Global Context is a feature of Gainsight PX that allows for storing metadata for a
 * session that automatically gets added to events. This feature helps us with users
 * in multiple companies by storing their metadata at the session/event level, not
 * just on their PX user record, which is overwritten when they switch acccounts.
 *
 * Reference:
 * https://support.gainsight.com/PX/Engagements/01On-boarding/Use_Global_Context
 */
export interface IGainsightGlobalContext extends Record<string, string | boolean | number> {
  currentUserId: string;
  currentUserIsImplementer: boolean;
  currentUserRoleCode: RoleCode;
  currentUserRoleName: string;
  currentPersonId: string;
  currentUserCompany: string;
  currentUserCompanyBos: BusinessOperatingSystem;
  isLeadership: boolean;
  ninetyPlatform: NinetyPlatform;
}

export interface IPXConfiguration {
  apiKey: string;
  /** Not in Gainsight's docs; the injected shim's page tracker reads it. See GainsightPXService.PX_CONFIG. */
  enabled?: boolean;
  enableLogs?: boolean;
  shouldTrackPages?: boolean;
  shouldTrackTapEvents?: boolean;
  reportTrackingIssues?: boolean;
  trackApplicationLifecycleEvents?: boolean;
  autoTrackHash?: boolean;
  flushQueueSize?: number;
  flushInterval?: number;
  host?: string;
  maxQueueSize?: number;
}

export interface IPXUser {
  id: string;
  email?: string;
  userhash?: string;
  gender?: string;
  lastName?: string;
  firstName?: string;
  signUpDate?: Date;
  title?: string;
  role?: string;
  subscriptionId?: string;
  phone?: string;
  countryCode?: string;
  countryName?: string;
  stateCode?: string;
  stateName?: string;
  city?: string;
  street?: string;
  continent?: string;
  postalCode?: string;
  regionName?: string;
  timeZone?: string;
  latitude?: number;
  longitude?: number;
  organization?: string;
  organizationEmployees?: string;
  organizationRevenue?: string;
  organizationIndustry?: string;
  organizationSicCode?: string;
  organizationDuns?: string;
  accountId?: string;
  firstVisitDate?: Date;
  score?: string;
  sfdcContactId?: string;
  customAttributes?: Record<string, any>;
}

export interface IPXAccount {
  id: string;
  name?: string;
  trackedSubscriptionId?: string;
  industry?: string;
  numberofEmployees?: number;
  sicCode?: string;
  website?: string;
  naicsCode?: string;
  plan?: string;
  countryCode?: string;
  countryName?: string;
  stateCode?: string;
  stateName?: string;
  city?: string;
  street?: string;
  continent?: string;
  postalCode?: string;
  regionName?: string;
  timeZone?: string;
  latitude?: number;
  longitude?: number;
  sfdcId?: string;
  customAttributes?: Record<string, any>;
}

export interface IPXIdentifyUser {
  user: IPXUser;
  account?: IPXAccount;
}

export interface IPXEngagementCallback {
  model?: any; // PXEngagementCallbackModel // no docs?
  errorMessage?: string;
}

export interface IPXErrorCallback {
  functionName: string;
  properties: string[];
  errorMessage: string;
}

export interface IPXEvent {
  eventName: string;
  properties: Record<string, string>;
}

/** Docs: https://support.gainsight.com/PX/Mobile/01Getting_Started/Install_Gainsight_PX/07_Gainsight_PX_Hybrid_Mobile_SDK#Initialize */
export interface IGainsightPx {
  initialize: (config: IPXConfiguration) => void;
  identifyWithID: (userId: string) => void;
  identifyUser: (ctx: IPXIdentifyUser) => void;
  setGlobalContext: (ctx: IGainsightGlobalContext) => void;
  reset: () => void;
  customEvent: (pxEvent: IPXEvent) => void;
  enterEditingMode: (url: string) => void;
  flush: () => void;
  enable: () => void;
  disable: () => void;
  hasGlobalContext: (id: string) => boolean;
  removeGlobalContextKeys: (keys: string[]) => void;
  hasGlobalContextKeyListener: (listener: (key: string, isExists: boolean) => void) => void;
  addEngagementListener: (listener: (engCallback: IPXEngagementCallback) => void) => void;
  removeEngagementListener: () => void;
  errorListener: (listener: (error: IPXErrorCallback) => void) => void;
}
